import type { Middleware } from 'openapi-fetch'

import { applyAuthHeaders } from './auth'
import { getSession } from '@/features/auth/session-store'
import { isPastRefreshThreshold } from '@/features/auth/tokens'
import { refreshSession } from '@/features/auth/refresh'
import { readErrorCode } from '@/api/error-body'

/**
 * A copy of each in-flight request, taken before it is sent.
 *
 * `fetch` consumes a request's body, so a request that comes back 401 cannot be
 * sent again -- the retry needs a copy made while the body was still there.
 * Keyed by the id openapi-fetch threads from `onRequest` through to `onResponse`.
 */
const retryable = new Map<string, Request>()

/**
 * Renews an expiring access token, and retries the one request that discovers the
 * token expired anyway.
 *
 * Both halves are needed. Renewing ahead of time keeps ordinary use from ever
 * seeing a 401, and covers the case a purely reactive scheme cannot: a tab left
 * open with a live websocket and no requests going out, whose token quietly
 * expires and whose next reconnect fails to authenticate. Retrying covers what
 * pre-emption cannot: a token invalidated early, or a clock far enough out that
 * the threshold was computed wrongly.
 */
export const refreshMiddleware: Middleware = {
  async onRequest({ request, id }) {
    const { authenticated, accessTokenPayload, tokenReceivedAt } = getSession()

    if (
      authenticated &&
      accessTokenPayload !== null &&
      isPastRefreshThreshold(accessTokenPayload, tokenReceivedAt)
    ) {
      // Deliberately not caught: a failed refresh has already ended the session,
      // and letting the request go out with a token known to be stale would only
      // turn one clear error into an unrelated-looking one.
      await refreshSession()
    }

    // Taken before the auth middleware runs, so the copy carries no credentials
    // and the retry is free to apply the renewed ones.
    retryable.set(id, request.clone())
    return request
  },

  async onResponse({ response, id, options }) {
    const original = retryable.get(id)
    retryable.delete(id)

    if (response.status !== 401 || original === undefined) {
      return undefined
    }

    const { authenticated } = getSession()
    if (!authenticated) {
      return undefined
    }

    // Reading the body here would consume it for the caller, so inspect a copy.
    const code = await readErrorCode(response.clone())
    if (code !== 'ERROR_INVALID_ACCESS_TOKEN') {
      return undefined
    }

    try {
      await refreshSession()
    } catch {
      // The session is gone; hand back the original 401 so the caller sees the
      // failure it actually got rather than one from the refresh attempt.
      return undefined
    }

    applyAuthHeaders(original.headers)
    // Straight to the underlying fetch rather than back through the client, so a
    // response that is somehow still a 401 cannot start this again.
    return await options.fetch(original)
  },

  onError({ id }) {
    retryable.delete(id)
    return undefined
  },
}

/** Test seam: the map is keyed by request and empties itself in normal use. */
export function __pendingRetryCountForTests(): number {
  return retryable.size
}
